import { PartConfiguratorTopLevelView } from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorTopLevelView.tsx";
import { ConfigurationCreationApi } from "src/api/generated/erp/configurator/api/configurationCreationApi";
import { PartConfigurationDetailsView } from "src/components/views/erp/configurator/inspecting/PartConfigurationDetailsView.tsx";
import i18n from "i18next";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { askObjectConfigurationHistoryEntryInput } from "src/components/views/erp/configurator/objectConfigurationHistory/ObjectConfigurationHistoryEntryInputForm.utils.tsx";
import { ShopOrderLinePartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/impl/shopOrderLinePartConfiguratorType.ts";
import {
	PART_CONFIGURATION_NOT_SAVED,
	PartConfiguratorTreeResult,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorTreeStatefulView.tsx";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { CloseDialogFunc } from "src/components/common/dialogs/GenericDialogContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { getPartConfiguratorAgentContextData } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeUtils.ts";

export interface ShopOrderLineConfiguratorViewProps {
	configuratorType: ShopOrderLinePartConfiguratorType;
	closeDialog: CloseDialogFunc;
	refreshSourceView?: () => void;
	disabled?: boolean;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

export const ShopOrderLineConfiguratorView = ({
	configuratorType,
	closeDialog,
	refreshSourceView,
	disabled,
	onDataDirtyStateChanged,
}: ShopOrderLineConfiguratorViewProps) => {
	const { openDialog } = useGenericDialog();
	const shopOrderLineId = configuratorType.shopOrderLineId;

	return (
		<PartConfiguratorTopLevelView
			configuratorType={configuratorType}
			readonly={disabled}
			onDataDirtyStateChanged={onDataDirtyStateChanged}
			onCompleted={async (result) => {
				if (result.type === "success") {
					return await onSubmit(result.value);
				} else {
					await closeDialog?.({ confirmIfEdited: true });
				}
			}}
		/>
	);

	async function onSubmit({
		propertyValues,
		propertyInputValues,
		configurationSessionId,
		configuratorInput,
		checkpointState,
	}: PartConfiguratorTreeResult) {
		const historyEntryInput = await askObjectConfigurationHistoryEntryInput({
			openDialog,
			objectType: "SHOP_ORDER_LINE",
			objectId: shopOrderLineId,
			configurationSessionId,
			configuratorType: configuratorType,
			configuratorInput,
			checkpointState,
		});
		if (historyEntryInput === undefined) return PART_CONFIGURATION_NOT_SAVED;

		const partConfigurationId = await ConfigurationCreationApi.createPartConfigurationForShopOrderLine({
			shopOrderLineId: shopOrderLineId,
			propertyValues: propertyValues,
			propertyInputValues: propertyInputValues,
			configurationSessionId: configurationSessionId,
			historyEntryInput: historyEntryInput,
		});

		await closeDialog();
		refreshSourceView?.();

		openDialog(() => ({
			title: i18n.t("configuration"),
			content: (
				<PartConfigurationDetailsView
					partConfigurationId={partConfigurationId}
					partConfiguratorType={configuratorType}
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.shopOrderLinePartConfiguration.dialog"),
				title: i18n.t("configuration"),
				data: {
					...getPartConfiguratorAgentContextData(configuratorType),
					partConfigurationId: partConfigurationId,
				},
			},
		}));
	}
};
